<template>
  <!-- Notifications panel next to the sidebar (US 9372). Loads 20 at a time; reading doesn't mark anything as read. -->
  <div class="np-wrap">
    <div class="np-backdrop" @click="$emit('close')"></div>
    <aside class="np"
           role="dialog"
           :aria-label="$t('notifications.page_title')"
           @keydown.esc="$emit('close')"
    >
      <div class="np__head">
        <h3 class="np__title">{{ $t('notifications.page_title') }}</h3>
        <button type="button"
                class="np__close"
                ref="close"
                @click="$emit('close')"
                :aria-label="$t('app_nav.close')"
        >
          <font-awesome-icon icon="times"/>
        </button>
      </div>

      <div class="np__tabs">
        <button type="button"
                class="np__tab"
                :class="{ 'np__tab--on': status === 'unread' }"
                @click="setStatus('unread')"
                qid="notifications-tab-unread"
        >
          {{ $t('app_nav.notifications_unread') }}<template v-if="unreadCount"> · {{ unreadLabel }}</template>
        </button>
        <button type="button"
                class="np__tab"
                :class="{ 'np__tab--on': status === 'all' }"
                @click="setStatus('all')"
                qid="notifications-tab-all"
        >
          {{ $t('app_nav.notifications_all') }}
        </button>
        <span class="np__spacer"></span>
        <button type="button"
                class="np__link"
                v-if="unreadCount"
                @click="markAllRead"
                qid="notifications-mark-all-read"
        >
          {{ $t('app_nav.notifications_mark_all_read') }}
        </button>
      </div>

      <div class="np__list" ref="list" @scroll="onScroll">
        <template v-for="group in groups">
          <div class="np__group" :key="'g-' + group.key">{{ $t(group.label) }}</div>
          <NotificationItem v-for="notification in group.items"
                            :key="notification.id"
                            :notification="notification"
                            @open="open"
          />
        </template>

        <div class="np__empty" v-if="!loading && !items.length">
          {{ status === 'unread' ? $t('app_nav.notifications_empty_unread') : $t('app_nav.notifications_empty_all') }}
        </div>
        <div class="np__loading" v-if="loading">
          <font-awesome-icon icon="spinner" class="fa-spin"/>
        </div>
        <button type="button"
                class="np__more"
                v-if="cursor && !loading"
                @click="load"
        >
          {{ $t('app_nav.notifications_load_more') }}
        </button>
      </div>

      <div class="np__foot">
        <router-link to="/notifications"
                     class="np__link"
                     @click.native="$emit('close')"
                     qid="notifications-page-link"
        >
          {{ $t('app_nav.notifications_open_all') }}
        </router-link>
      </div>
    </aside>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import NotificationItem from '@/components/notification/NotificationItem.vue';

const PER_PAGE = 20;

export default {
  name: 'NotificationsPanel',
  components: {
    NotificationItem
  },
  props: {
    unreadCount: {
      type: Number,
      default: 0
    },
    unreadCapped: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      status: this.unreadCount ? 'unread' : 'all',
      items: [],
      cursor: null,
      loading: false,
      requestId: 0,
    }
  },
  computed: {
    unreadLabel() {
      return this.unreadCapped ? '99+' : this.unreadCount;
    },
    // Today, This week, Older
    groups() {
      let startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      let startOfWeek = new Date(startOfToday.getTime() - 6 * 24 * 60 * 60 * 1000);
      let groups = [
        { key: 'today', label: 'app_nav.notifications_today', items: [] },
        { key: 'week', label: 'app_nav.notifications_this_week', items: [] },
        { key: 'older', label: 'app_nav.notifications_older', items: [] },
      ];
      this.items.forEach(function(item) {
        let created = new Date(item.created_at);
        let group = created >= startOfToday ? groups[0] : (created >= startOfWeek ? groups[1] : groups[2]);
        group.items.push(item);
      });
      return groups.filter(g => g.items.length);
    }
  },
  methods: {
    setStatus(status) {
      if (this.status === status) {
        return;
      }
      this.status = status;
      this.items = [];
      this.cursor = null;
      this.load();
    },
    load() {
      let self = this;
      let requestId = ++this.requestId;
      this.loading = true;
      piincHttp.get('users/me/notifications', { params: {
        status: this.status,
        per_page: PER_PAGE,
        cursor: this.cursor || undefined
      }}).then(function(response) {
        // Ignore answers to a tab that is no longer shown
        if (requestId !== self.requestId) {
          return;
        }
        self.items.push(...response.data.data);
        self.cursor = response.data.next_cursor;
        self.loading = false;
      }, function() {
        self.loading = false;
      });
    },
    onScroll() {
      let list = this.$refs.list;
      if (this.cursor && !this.loading && list.scrollTop + list.clientHeight >= list.scrollHeight - 80) {
        this.load();
      }
    },
    // Clicking a notification marks it as read. A link inside it navigates, and the sidebar closes the
    // panel when the route changes.
    open(notification) {
      let self = this;
      if (!notification.read_at) {
        notification.read_at = new Date().toISOString();
        piincHttp.put('users/me/notifications/' + notification.id + '/read').then(function() {
          self.$emit('changed');
        }, function() {});
      }
    },
    markAllRead() {
      let self = this;
      piincHttp.put('users/me/notifications/read').then(function() {
        let now = new Date().toISOString();
        self.items.forEach(function(item) {
          if (!item.read_at) {
            item.read_at = now;
          }
        });
        self.$emit('changed');
      }, function() {});
    }
  },
  created() {
    this.load();
  },
  mounted() {
    this.$refs.close.focus();
  }
}
</script>
